import React from 'react';
import PropTypes from 'prop-types';
import {
    PlayResultsWrapper,
    PlayResultsEvent,
    PlayResultsDescription,
    PlayResultsHeadshotWrapper,
    HomeRunDetailsWrapper,
    HomeRunWrapper,
    HomeRunSubHeader,
    HomeRunSubText
} from './PlayResults.style';
import PlayStatus, { basesResultsShape } from './utils/PlayStatus';
import PlayVideo, { videoShape } from './playResults/PlayVideo';
import WinProbability from './playResults/WinProbability';
import Headshot from '@mlb/react-patterns/v4/components/Headshot/headshot';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import { useTranslator } from '@gameday/utils/hooks/useTranslator';
import Link from '@mlb/react-patterns/v4/components/Link/link';
import buildAtBatOverlay from '@gameday/utils/buildAtBatOverlay';
import { useSelector } from 'react-redux';
import { getContextData } from '@gameday/utils/selectors/getContextData';
import buildPlayerUrl from '@gameday/utils/buildPlayerUrl';

const generateHomeRunStat = (label, value, unitLabel) => (
    <HomeRunWrapper>
        <HomeRunSubHeader>
            {label}
        </HomeRunSubHeader>
        <HomeRunSubText>
            {value}<span>{unitLabel}</span>
        </HomeRunSubText>
    </HomeRunWrapper>
);

const HeadshotComponent = (batterId, headshotType) => (
    <Headshot
        width='48px'
        height='48px'
        playerId={batterId}
        type={headshotType}
        zoomScale={1}
        ratio='1:1'
    />
);

export const buildHomeRunDisplay = (hitData, translatedHomeRunsTitle) => {
    if (hitData) {
        const {
            totalDistance,
            launchSpeed,
            launchAngle
        } = hitData;
        const {
            distanceLabel,
            exitVelocityLabel,
            launchAngleLabel
        } = translatedHomeRunsTitle;

        // those values are numbers, and 0 is a valid number
        const validTotalDistance = typeof totalDistance !== 'undefined' && totalDistance !== null;
        const validLaunchSpeed = typeof launchSpeed !== 'undefined' && launchSpeed !== null;
        const validLaunchAngle = typeof launchAngle !== 'undefined' && launchAngle !== null;

        if (validTotalDistance || validLaunchSpeed || validLaunchAngle) {
            // TODO: Translate labels and unit labels
            return (
                <HomeRunDetailsWrapper>
                    {validLaunchSpeed && generateHomeRunStat(exitVelocityLabel, launchSpeed, ' mph')}
                    {validTotalDistance && generateHomeRunStat(distanceLabel, totalDistance, ' ft')}
                    {validLaunchAngle && generateHomeRunStat(launchAngleLabel, launchAngle, ' deg')}
                </HomeRunDetailsWrapper>
            );
        }
    }
};

export const buildPlayerHeadshot = (offset, isLoading, url, deviceInteraction, batterId, headshotType, disablePlayerLinks) => (
    <PlayResultsHeadshotWrapper
        $offset={offset}
    >
        {!isLoading
            ? !disablePlayerLinks
                ? (
                    <Link
                        url={url}
                        handlers={{
                            onClick: (e) => {
                                if (deviceInteraction.playercard({ title: 'Player Card', url, pid: batterId })) {
                                    e.preventDefault();
                                }
                            }
                        }}
                        target='_blank'
                    >
                        {HeadshotComponent(batterId, headshotType)}
                    </Link>
                )
                : HeadshotComponent(batterId, headshotType)
            : <Skeleton width='48px' height='48px' isCircle={true} />
        }
    </PlayResultsHeadshotWrapper>
);

const PlayResults = ({
    result = {},
    outCount,
    offset,
    batterId = '',
    hitData,
    basesResults,
    resultVideo = {},
    count,
    keymoments,
    halfInning,
    awayTeamName,
    homeTeamName,
    isLoading = false,
    language = 'en',
    deviceInteraction = {},
    openAtBatOverlay,
    atBatIndex
}) => {
    const {
        event = 'Result',
        description,
        eventType
    } = result;

    const {
        templateUrl,
        blurb,
        duration,
        slug,
        title,
        playbacks
    } = resultVideo;
    const getPlayerContextData = useSelector(getContextData);
    const { headshotType, disablePlayerLinks, playerPageContext } = getPlayerContextData(batterId);

    const translatedHomeRunsTitle = {
        distanceLabel: useTranslator('label_distance', 'Distance'),
        exitVelocityLabel: useTranslator('label_exit_velocity', 'Exit Velocity'),
        launchAngleLabel: useTranslator('label_launch_angle', 'Launch Angle')
    };

    const url = buildPlayerUrl({ nameSlug: batterId, language, playerPageContext });

    return (
        <PlayResultsWrapper
            $offset={offset}
        >
            {buildPlayerHeadshot(offset, isLoading, url, deviceInteraction, batterId, headshotType, disablePlayerLinks)}

            <PlayResultsEvent
                type={eventType}
                isLoading={isLoading}
            >
                {!isLoading ? buildAtBatOverlay(batterId, atBatIndex, event, openAtBatOverlay) : <Skeleton width='149px' height='35px' margin='0px' />}
            </PlayResultsEvent>

            {!isLoading
                ? buildAtBatOverlay(
                    batterId,
                    atBatIndex,
                    <PlayStatus
                        outCount={outCount}
                        basesResults={basesResults}
                        count={count}
                    />,
                    openAtBatOverlay
                )
                : <Skeleton width='100%' height='11px' margin='0px' />
            }
            <PlayResultsDescription>
                {!isLoading ? buildAtBatOverlay(batterId, atBatIndex, description, openAtBatOverlay) : <Skeleton width='100%' height='11px' margin='0px' />}
            </PlayResultsDescription>

            {eventType === 'home_run' ? buildHomeRunDisplay(hitData, translatedHomeRunsTitle) : undefined}

            {!isLoading && templateUrl && (
                <PlayVideo
                    templateUrl={templateUrl}
                    blurb={blurb}
                    duration={duration}
                    id={slug}
                    title={title}
                    slug={slug}
                    width={'286px'}
                    height={'162px'}
                    playbacks={playbacks}
                    playerContext={'Live Tab'}
                />
            )}

            {isLoading && <Skeleton width='100%' height='232px' margin='0px' />}

            {keymoments && (
                <WinProbability
                    keymoments={keymoments}
                    halfInning={halfInning}
                    awayTeamName={awayTeamName}
                    homeTeamName={homeTeamName}
                />
            )}
        </PlayResultsWrapper>
    );
};

PlayResults.propTypes = {
    result: PropTypes.object,
    outCount: PropTypes.number,
    offset: PropTypes.bool,
    batterId: PropTypes.number,
    hitData: PropTypes.object,
    basesResults: PropTypes.shape(basesResultsShape),
    resultVideo: PropTypes.shape(videoShape),
    count: PropTypes.object,
    keymoments: PropTypes.object,
    halfInning: PropTypes.string,
    awayTeamName: PropTypes.string,
    homeTeamName: PropTypes.string,
    isLoading: PropTypes.bool,
    language: PropTypes.string,
    deviceInteraction: PropTypes.object,
    atBatIndex: PropTypes.number,
    openAtBatOverlay: PropTypes.func
};

export default PlayResults;
